"use client"

import type { ReactNode } from "react"
import { memo } from "react"
import {
	IconPencilOutline18 as Pencil,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { ProjectIconKeys } from "../icons/project-icons"
import { ProjectIcons } from "../icons/project-icons"
import { IconButton } from "./icon-button"
import { Tooltip } from "./tooltip"

export interface ChangelogTagBadgeProps {
	tag: { id: string; name: string; color: string; icon?: string | null }
	/** Render the icon — caller provides this since icon resolution differs per app */
	renderIcon?: (tag: { icon?: string | null; color: string }) => ReactNode
	onClick?: () => void
	onEdit?: () => void
	onRemove?: () => void
	className?: string
}

export const ChangelogTagBadge = memo(
	({
		tag,
		renderIcon,
		onClick,
		onEdit,
		onRemove,
		className,
	}: ChangelogTagBadgeProps) => {
		const renderedIcon = renderIcon?.(tag)
		const builtInIcon = tag.icon
			? ProjectIcons[tag.icon as ProjectIconKeys]
			: null
		const icon =
			renderedIcon ??
			(builtInIcon ? (
				(() => {
					const Icon = builtInIcon
					return <Icon className="h-3 w-3" style={{ color: tag.color }} />
				})()
			) : (
				<span
					className="inline-block h-2 w-2 rounded-full"
					style={{ backgroundColor: tag.color }}
				/>
			))

		const hasActions = onEdit || onRemove

		return (
			<div
				role={onClick ? "button" : undefined}
				tabIndex={onClick ? 0 : undefined}
				onClick={onClick}
				onKeyDown={
					onClick
						? (e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault()
									onClick()
								}
							}
						: undefined
				}
				className={cn(
					"group relative flex cursor-default items-center gap-1.5 rounded-full border border-gray-200 py-0.5 pl-2 pr-2.5 text-xs font-medium dark:border-gray-700",
					onClick && "hover:opacity-80",
					className,
				)}
			>
				{icon}
				<span className="truncate">{tag.name}</span>
				{hasActions && (
					<div
						className={cn(
							"absolute right-0.5 flex items-center gap-1 rounded-full bg-white px-1 opacity-0 group-hover:opacity-100 dark:bg-[#0F0F12]",
							{
								"right-1 px-0": tag.name.length < 3,
							},
						)}
					>
						{onEdit && (
							<Tooltip label="Edit tag">
								<IconButton
									onClick={(e) => {
										e.stopPropagation()
										onEdit()
									}}
									variant="ghost"
									Icon={Pencil}
									className="size-3.5 rounded-sm"
									iconClassName="size-3"
								/>
							</Tooltip>
						)}
						{onRemove && (
							<Tooltip label="Remove tag">
								<IconButton
									onClick={(e) => {
										e.stopPropagation()
										onRemove()
									}}
									variant="ghost"
									Icon={X}
									className="size-3.5 rounded-sm"
									iconClassName="size-3"
								/>
							</Tooltip>
						)}
					</div>
				)}
			</div>
		)
	},
)
